import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { whatsappLink, scrollToId, IMAGES } from "@/data";

const LINES = [
    { text: "Higienização", accent: false },
    { text: "profissional para", accent: false },
    { text: "cuidar do que é", accent: false },
    { text: "importante para você.", accent: true },
];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-screen bg-navy noise-bg overflow-hidden flex items-center pt-[72px]"
        >
            <div
                className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-brand/10 blur-[140px] pointer-events-none"
                aria-hidden="true"
            />
            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center w-full"
            >
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.7 }}
                        className="flex justify-center lg:justify-start mb-8"
                    >
                        <img
                            src="/logo-mark.png"
                            alt="Brandão Morais Higienização"
                            className="h-24 sm:h-28 w-auto"
                        />
                    </motion.div>

                    <h1
                        data-testid="hero-title"
                        className="font-display font-bold text-white tracking-tighter text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl"
                    >
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.accent ? "text-brand" : ""}`}
                                    initial={{ y: "115%" }}
                                    animate={{ y: "0%" }}
                                    transition={{
                                        duration: 1,
                                        delay: 0.35 + i * 0.13,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        data-testid="hero-subtitle"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.8 }}
                        className="mt-7 text-white/70 text-base sm:text-lg leading-relaxed max-w-xl"
                    >
                        Qualidade, cuidado e confiança em cada serviço.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.15, duration: 0.8 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-quote-button"
                            onClick={() => scrollToId("contato")}
                            className="group inline-flex items-center gap-3 bg-brand hover:bg-brand-hover text-white font-bold px-8 py-4 rounded-full transition-colors duration-300"
                        >
                            Solicitar orçamento
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                        </button>
                        <a
                            data-testid="hero-whatsapp-button"
                            href={whatsappLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 border border-white/25 hover:border-brand hover:text-brand text-white font-bold px-8 py-4 rounded-full transition-colors duration-300"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Falar pelo WhatsApp
                        </a>
                    </motion.div>
                </div>

                <div className="lg:col-span-5 relative">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        <div className="absolute -inset-3 border border-brand/30 rounded-[2rem] pointer-events-none" aria-hidden="true" />
                        <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-[1.75rem]">
                            <video
                                data-testid="hero-video"
                                poster={IMAGES.heroSofa}
                                autoPlay
                                muted
                                loop
                                playsInline
                                className="w-full h-[420px] sm:h-[520px] object-cover scale-110"
                                aria-label="Vídeo real da Brandão Morais Higienização em ação"
                            >
                                <source src="/videos/hero-acao.webm" type="video/webm" />
                                <source src="/videos/hero-acao.mp4" type="video/mp4" />
                            </video>
                            <div className="absolute inset-0 bg-navy/20" aria-hidden="true" />
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.4, duration: 0.8 }}
                            className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl px-6 py-4 shadow-2xl"
                        >
                            <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-navy/50">
                                Atendimento via
                            </p>
                            <p className="font-display font-bold text-navy text-lg leading-tight">
                                WhatsApp <span className="text-brand">•</span> Orçamento rápido
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2"
                aria-hidden="true"
            >
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-bold">
                    Role para explorar
                </span>
                <span className="w-px h-10 bg-gradient-to-b from-brand to-transparent" />
            </motion.div>
        </section>
    );
};
